// src/dashboard/pages/teacher/Techniques/hooks/useTechniquesDashboard.js

import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import {
  getAllTechniques,
  addTechnique,
  updateTechnique,
  deleteTechnique,
} from "../../../../../services/techniquesServices";

import { getBeltsList } from "../../../../../services/beltServices";

import { uploadTechniquePhoto } from "../../../../../services/techniquesPhotoServices";

import {
  buildTechniquePayload,
  calculateTechniqueStats,
  filterTechniques,
  getBeltOptions,
  getFamilyOptions,
  sortTechniques,
} from "../utils/techniquesDashboardUtils";

const defaultFilters = {
  query: "",
  family: "",
  belt: "",
  media: "",
  sort: "name_asc",
};

export function useTechniquesDashboard() {
  const [techniques, setTechniques] = useState([]);
  const [belts, setBelts] = useState([]);

  const [filters, setFilters] = useState(defaultFilters);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const [addDrawerOpen, setAddDrawerOpen] = useState(false);
  const [editDrawerOpen, setEditDrawerOpen] = useState(false);
  const [currentTechnique, setCurrentTechnique] = useState(null);

  const fetchTechniques = useCallback(async () => {
    try {
      setLoading(true);

      const response = await getAllTechniques();
      const rows = Array.isArray(response) ? response : [];

      setTechniques(sortTechniques(rows, "name_asc"));
    } catch (error) {
      console.error("Erreur chargement techniques :", error);
      toast.error("Erreur lors du chargement des techniques.");
      setTechniques([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchBelts = useCallback(async () => {
    try {
      const response = await getBeltsList();
      setBelts(Array.isArray(response) ? response : []);
    } catch (error) {
      console.error("Erreur chargement ceintures :", error);
      setBelts([]);
    }
  }, []);

  useEffect(() => {
    fetchTechniques();
    fetchBelts();
  }, [fetchTechniques, fetchBelts]);

  const familyOptions = useMemo(() => {
    return getFamilyOptions(techniques);
  }, [techniques]);

  const beltOptions = useMemo(() => {
    return getBeltOptions(belts, techniques);
  }, [belts, techniques]);

  const filteredTechniques = useMemo(() => {
    return filterTechniques(techniques, filters);
  }, [techniques, filters]);

  const stats = useMemo(() => {
    return calculateTechniqueStats(techniques, filteredTechniques);
  }, [techniques, filteredTechniques]);

  const openAddDrawer = () => {
    setAddDrawerOpen(true);
  };

  const closeAddDrawer = () => {
    if (saving) return;
    setAddDrawerOpen(false);
  };

  const openEditDrawer = (technique) => {
    setCurrentTechnique(technique);
    setEditDrawerOpen(true);
  };

  const closeEditDrawer = () => {
    if (saving) return;
    setCurrentTechnique(null);
    setEditDrawerOpen(false);
  };

  const handleCreateTechnique = async (formData) => {
    try {
      setSaving(true);

      const payload = buildTechniquePayload(formData);
      await addTechnique(payload);

      toast.success("Technique ajoutée avec succès.");
      setAddDrawerOpen(false);

      await fetchTechniques();
    } catch (error) {
      console.error("Erreur création technique :", error);
      toast.error("Erreur lors de la création de la technique.");
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateTechnique = async (id, formData) => {
    if (!id) {
      toast.error("Technique introuvable.");
      return;
    }

    try {
      setSaving(true);

      const payload = buildTechniquePayload(formData);
      await updateTechnique(id, payload);

      toast.success("Technique mise à jour.");
      setEditDrawerOpen(false);
      setCurrentTechnique(null);

      await fetchTechniques();
    } catch (error) {
      console.error("Erreur mise à jour technique :", error);
      toast.error("Erreur lors de la mise à jour de la technique.");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteTechnique = async (itemOrId) => {
    const id = typeof itemOrId === "object" ? itemOrId?.id : itemOrId;

    if (!id) {
      toast.error("Technique introuvable.");
      return;
    }

    if (!window.confirm("Supprimer cette technique ?")) {
      return;
    }

    try {
      setDeletingId(id);

      await deleteTechnique(id);

      toast.success("Technique supprimée.");

      setTechniques((prev) => prev.filter((item) => item.id !== id));

      if (currentTechnique?.id === id) {
        setCurrentTechnique(null);
        setEditDrawerOpen(false);
      }
    } catch (error) {
      console.error("Erreur suppression technique :", error);
      toast.error("Erreur lors de la suppression de la technique.");
    } finally {
      setDeletingId(null);
    }
  };

  const handleUploadTechniquePhoto = async ({
    techniqueId,
    file,
    format,
    onUploadProgress,
  }) => {
    if (!techniqueId) {
      toast.error("Technique introuvable.");
      return null;
    }

    if (!file) {
      toast.error("Veuillez sélectionner une image.");
      return null;
    }

    try {
      setSaving(true);

      const response = await uploadTechniquePhoto(techniqueId, file, {
        format,
        onUploadProgress,
      });

      const imageUrl =
        response?.image1_url ||
        response?.technique?.image1_url ||
        response?.data?.image1_url ||
        "";

      if (imageUrl) {
        setTechniques((prev) =>
          prev.map((item) =>
            item.id === techniqueId ? { ...item, image1_url: imageUrl } : item
          )
        );

        setCurrentTechnique((prev) =>
          prev?.id === techniqueId ? { ...prev, image1_url: imageUrl } : prev
        );
      }

      toast.success("Image téléversée et optimisée.");

      await fetchTechniques();

      return imageUrl;
    } catch (error) {
      console.error("Erreur upload image technique :", error);
      toast.error("Erreur lors du téléversement de l’image.");
      return null;
    } finally {
      setSaving(false);
    }
  };

  const refreshTechniques = useCallback(async () => {
    await fetchTechniques();
  }, [fetchTechniques]);

  return {
    techniques,
    filteredTechniques,
    familyOptions,
    beltOptions,
    stats,

    filters,
    setFilters,

    loading,
    saving,
    deletingId,

    addDrawerOpen,
    editDrawerOpen,
    currentTechnique,

    openAddDrawer,
    closeAddDrawer,
    openEditDrawer,
    closeEditDrawer,

    handleCreateTechnique,
    handleUpdateTechnique,
    handleDeleteTechnique,
    handleUploadTechniquePhoto,
    refreshTechniques,
  };
}